import { LOCALE_EXEMPT_ROUTES } from '@/utils/publicRoutes';
import { redactSensitiveUrlParams } from '@/utils/redact-tracing-urls';
import { isSessionReplayBlockedRoute } from '@/utils/telemetry/replay-blocked-routes';
import { type PostHogConfig, type Properties } from 'posthog-js';

// Decided at init: rrweb's first snapshot carries the page URL before any
// effect runs, and `sanitize_properties` does not reach `$snapshot_data`.
export { isSessionReplayBlockedRoute };

const URL_PROPERTY_KEYS = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
] as const;

function redactUrlKeys(bag: Properties): Properties {
  const out: Properties = { ...bag };
  for (const key of URL_PROPERTY_KEYS) {
    const value: unknown = out[key];
    if (typeof value === 'string') out[key] = redactSensitiveUrlParams(value);
  }
  return out;
}

/**
 * posthog-js stamps the page URL on every capture and on the person profile.
 * Only Datadog RUM runs the query-string redactor otherwise, so run it here
 * too: auth callbacks and Auth0's hosted onboarding redirect carry `?state=…`.
 */
export function redactPosthogUrlProperties(properties: Properties): Properties {
  const out = redactUrlKeys(properties);
  for (const bag of ['$set', '$set_once'] as const) {
    const inner: unknown = out[bag];
    if (inner && typeof inner === 'object') out[bag] = redactUrlKeys(inner as Properties);
  }
  return out;
}

/** The EHR launch landing routes; the middleware keeps them free of a locale prefix. */
export const isEhrLaunchRoute = (pathname: string): boolean =>
  LOCALE_EXEMPT_ROUTES.some((route) => pathname.startsWith(route));

/**
 * ml-scribe appends the launch user's id to the EHR launch landing URL. Read
 * nowhere else: a crafted link on another route would merge the visitor into
 * someone else's PostHog person and serve that person's flags.
 */
export function getLaunchKindeUserId(
  search: string,
  pathname: string
): string | undefined {
  if (!isEhrLaunchRoute(pathname)) return undefined;
  return new URLSearchParams(search).get('kinde_user_id')?.trim() || undefined;
}

/**
 * PostHog client init options shared by the module-load `posthog.init` call.
 *
 * Datadog RUM replay is the only desktop recorder. Init must disable PostHog
 * session recording *before* the SDK can start rrweb — a React-tree gate is
 * too late because posthog-provider inits at import time in every Tauri
 * webview (main window and overlays).
 *
 * `disable_session_recording: true` IS the guard, and it is durable — do not
 * remove it. Verified against the locked posthog-js 1.390.2 source:
 * `SessionRecording._isRecordingEnabled` reads
 * `!this._instance.config.disable_session_recording` live on every call, and
 * `SessionRecording.onRemoteConfig` always routes through
 * `startIfEnabledOrStop`, so a remote config that enables recording server-side
 * still cannot start the recorder while this flag is set.
 *
 * `stopSessionRecording()` in `loaded` is defence in depth only. It is
 * literally `set_config({ disable_session_recording: true })` — it re-asserts
 * the same flag init already set, so it adds nothing the flag does not already
 * do and CANNOT substitute for it.
 */
export function buildPosthogInitOptions(
  isDesktop: boolean,
  pathname?: string
): Partial<PostHogConfig> {
  const replayBlockedHere =
    pathname !== undefined && isSessionReplayBlockedRoute(pathname);
  const disableReplay = isDesktop || replayBlockedHere;
  return {
    disable_external_dependency_loading: true,
    api_host: 'https://sys.heidihealth.com',
    disable_web_experiments: false,
    ...(disableReplay
      ? {}
      : {
          session_recording: {
            recordCrossOriginIframes: true,
            maskTextSelector: '*',
            maskAllInputs: true,
          },
        }),
    sanitize_properties: redactPosthogUrlProperties,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: disableReplay,
    enable_recording_console_log: !isDesktop,
    mask_all_text: true,
    loaded: (ph) => {
      ph.debug(process.env.NEXT_PUBLIC_POSTHOG_DEBUG === 'true');
      if (disableReplay) {
        ph.stopSessionRecording();
      }
    },
  };
}
